import { useCallback, useEffect, useState } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  useRouterState,
  Link,
  Outlet,
} from "@tanstack/react-router";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clapperboard, Monitor, Moon, Sun } from "lucide-react";
import { BRAND, type Locale } from "@rec/shared";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui";
import { Library } from "@/pages/Library";
import { Recording } from "@/pages/Recording";
import { SearchPage } from "@/pages/SearchPage";
import { Settings } from "@/pages/Settings";
import { Members } from "@/pages/Members";
import { api } from "@/lib/api";
import { AcceptInvitation } from "@/pages/AcceptInvitation";
import { Account } from "@/pages/Account";
import { ResetPassword } from "@/pages/ResetPassword";
import { VerifyEmail } from "@/pages/VerifyEmail";
import { BackOffice } from "@/pages/BackOffice";
import { Studio } from "@/pages/Studio";
import { StudioEditor } from "@/pages/StudioEditor";
import { AuthGate } from "@/components/AuthGate";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { signOut } from "@/lib/auth";
import {
  Film,
  House,
  LogOut,
  Menu,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { MediaLibrary } from "@/pages/MediaLibrary";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLocaleSync } from "@/i18n";
import { RouteError } from "@/components/RouteError";

/**
 * Bring this device and the account into agreement about the language.
 * Mounted inside AuthGate, so it only runs for someone signed in.
 */
function LocaleSync() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 5 * 60_000 });
  const save = useCallback(
    (locale: Locale) =>
      api.updateMyLocale(locale).then(() =>
        queryClient.setQueryData(["me"], (old: { locale: string | null } | undefined) =>
          old ? { ...old, locale } : old,
        ),
      ),
    [queryClient],
  );
  useLocaleSync(me.data, save);
  return null;
}

function ThemeToggle() {
  const { t } = useTranslation("shell");
  const { theme, cycle } = useTheme();

  const { icon, label } = {
    light: { icon: <Sun size={16} strokeWidth={2} />, label: t("theme.light") },
    dark: { icon: <Moon size={16} strokeWidth={2} />, label: t("theme.dark") },
    system: { icon: <Monitor size={16} strokeWidth={2} />, label: t("theme.system") },
  }[theme];

  return (
    <Button variant="ghost" size="icon" onClick={cycle} title={label} aria-label={label}>
      {icon}
    </Button>
  );
}

/**
 * Routes that must render for somebody who cannot sign in.
 *
 * Accepting an invitation creates the account; resetting a password is for
 * someone locked out of theirs. AuthGate would send both to a sign-in form they
 * cannot get through, which is how both flows were dead: the emails linked to
 * routes that did not exist at all.
 */
const PUBLIC_PATHS = ["/accept-invitation", "/reset-password", "/verify-email"];

function RootLayout() {
  const { t } = useTranslation("shell");
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (PUBLIC_PATHS.some((prefix) => pathname.startsWith(prefix))) {
    // No shell either — the workspace switcher and sign-out button are
    // meaningless to someone who has neither.
    return <Outlet />;
  }

  return (
    <AuthGate>
    <LocaleSync />
    <div className="min-h-screen bg-bg">
      <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[1500px] items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[15px] font-semibold tracking-tight text-text hover:opacity-80"
            >
              {BRAND.name}
            </Link>
            <span className="text-border-strong">/</span>
            <WorkspaceSwitcher />
          </div>

          <nav className="hidden items-center gap-1 sm:flex">
            <NavLink to="/" icon={<House size={14} />} label={t("nav.home")} exact />
            <NavLink
              to="/library"
              icon={<Film size={14} />}
              label={t("nav.library")}
            />
            <NavLink
              to="/studio"
              icon={<Clapperboard size={14} />}
              label={t("nav.studio")}
            />
            <NavLink to="/search" icon={<Search size={14} />} label={t("nav.search")} />
            <NavLink to="/people" icon={<Users size={14} />} label={t("nav.people")} />
            <NavLink
              to="/settings"
              icon={<SettingsIcon size={14} />}
              label={t("nav.settings")}
            />
            <OperatorNav />
          </nav>
          <div className="flex items-center gap-1">
            <MobileNav />
            <Link
              to="/account"
              title={t("nav.account")}
              aria-label={t("nav.account")}
              className="rounded-lg p-2 text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
              activeProps={{ className: "bg-bg-subtle text-text" }}
            >
              <UserRound size={16} strokeWidth={2} />
            </Link>
            <LanguageSwitcher />
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void signOut().then(() => window.location.reload())}
              title={t("nav.signOut")}
              aria-label={t("nav.signOut")}
            >
              <LogOut size={16} strokeWidth={2} />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 py-7">
        <Outlet />
      </main>
    </div>
    </AuthGate>
  );
}

/**
 * Nav item that marks itself active. TanStack Router's `activeProps` handles
 * the match, so the highlight cannot drift from the actual route.
 */
function NavLink({
  to,
  icon,
  label,
  exact,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  /** For "/" — without it the root counts as active on every route. */
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      activeOptions={exact ? { exact: true } : undefined}
      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
      activeProps={{ className: "bg-bg-subtle text-text" }}
    >
      {icon}
      {label}
    </Link>
  );
}

/**
 * The back-office link, for platform operators only.
 *
 * Hiding it is presentation — the API refuses every call underneath it to
 * anyone else — but a link that 403s is not a feature, and an operator with no
 * link at all is how this went unnoticed: the route existed and was reachable,
 * and nothing anywhere pointed at it.
 */
/**
 * The same links, for screens too narrow for the bar.
 *
 * Below 640px the nav was `hidden` with nothing in its place, so Search, People,
 * Settings and the back-office had no entry point at all on a phone — the app
 * was one screen with no way off it.
 */
function MobileNav() {
  const { t } = useTranslation("shell");
  const [open, setOpen] = useState(false);
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 5 * 60_000 });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="sm:hidden">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
        aria-expanded={open}
      >
        {open ? <X size={17} /> : <Menu size={17} />}
      </Button>

      {open ? (
        <>
          <div
            className="fixed inset-0 top-14 z-40 bg-black/30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <nav className="fixed inset-x-0 top-14 z-50 border-b border-border bg-surface p-2 shadow-[var(--shadow-md)]">
            <MobileLink to="/" label={t("nav.home")} onNavigate={() => setOpen(false)} />
            <MobileLink to="/library" label={t("nav.library")} onNavigate={() => setOpen(false)} />
            <MobileLink to="/search" label={t("nav.search")} onNavigate={() => setOpen(false)} />
            <MobileLink to="/studio" label={t("nav.studio")} onNavigate={() => setOpen(false)} />
            <MobileLink to="/people" label={t("nav.people")} onNavigate={() => setOpen(false)} />
            <MobileLink
              to="/settings"
              label={t("nav.settings")}
              onNavigate={() => setOpen(false)}
            />
            <MobileLink
              to="/account"
              label={t("nav.account")}
              onNavigate={() => setOpen(false)}
            />
            {me.data?.isPlatformOperator ? (
              <MobileLink
                to="/admin"
                label={t("nav.backOffice")}
                onNavigate={() => setOpen(false)}
              />
            ) : null}
          </nav>
        </>
      ) : null}
    </div>
  );
}

function MobileLink({
  to,
  label,
  onNavigate,
}: {
  to: string;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="block rounded-lg px-3 py-2.5 text-[14px] text-text transition-colors hover:bg-surface-hover"
      activeProps={{ className: "bg-bg-subtle font-medium" }}
    >
      {label}
    </Link>
  );
}

function OperatorNav() {
  const { t } = useTranslation("shell");
  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    // Operator status changes about once ever; re-asking on every navigation is
    // a request per page for an answer that does not move.
    staleTime: 5 * 60_000,
  });

  if (!me.data?.isPlatformOperator) return null;

  return <NavLink to="/admin" icon={<ShieldCheck size={14} />} label={t("nav.backOffice")} />;
}

const rootRoute = createRootRoute({
  component: RootLayout,
  // Without one, a render error showed the router's bare default and a
  // console warning that nothing caught it.
  errorComponent: RouteError,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Library,
});

const mediaLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library",
  component: MediaLibrary,
});

const recordingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/r/$recordingId",
  component: Recording,
  // `?t=` deep links carry milliseconds, matching how every timestamp is stored.
  validateSearch: z.object({ t: z.number().int().nonnegative().optional() }),
});

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio",
  component: Studio,
});

const studioEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio/$projectId",
  component: StudioEditor,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: SearchPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: Settings,
});

const membersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/people",
  component: Members,
});

// Back-office. The API re-checks the permission on every call — this route
// existing does not grant anything.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: BackOffice,
});

/**
 * Accepting an invitation, deliberately outside the authenticated shell.
 *
 * The person following this link has no account and no workspace, so the app
 * chrome — workspace switcher, nav, sign-out — is meaningless to them, and
 * AuthGate would bounce them to sign in before they could accept.
 */
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: Account,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmail,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPassword,
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invitation/$token",
  component: AcceptInvitation,
});

const routeTree = rootRoute.addChildren([
  mediaLibraryRoute,
  studioRoute,
  studioEditorRoute,
  acceptInvitationRoute,
  accountRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  indexRoute,
  recordingRoute,
  searchRoute,
  settingsRoute,
  membersRoute,
  adminRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
